/* InternalSupplies.jsx — manages "non-saleable" stock used by the store
 * itself (till rolls, A4 paper, printer cartridges, refuse bags, sealing
 * tape, etc.). Reorder alerts work like the main Stock Master, but GRV
 * costs for these items are auto-routed to the Income Statement as
 * "Operating Supplies" expense (not booked as inventory asset).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Box, AlertTriangle, AlertCircle, CheckCircle2, Search, RefreshCw, Plus, TrendingDown, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const urgencyBadge = (u) => {
  if (u === "critical") return <Badge className="bg-red-600 hover:bg-red-600">Critical</Badge>;
  if (u === "low") return <Badge className="bg-amber-500 hover:bg-amber-500">Low</Badge>;
  if (u === "ok_overstocked") return <Badge variant="outline" className="border-blue-300 text-blue-700">Overstocked</Badge>;
  return <Badge variant="outline" className="border-green-300 text-green-700">OK</Badge>;
};

export default function InternalSupplies() {
  const [data, setData] = useState({ items: [], summary: { critical: 0, low: 0, ok: 0, total: 0 } });
  const [trend, setTrend] = useState({ months: [], total: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [productMatches, setProductMatches] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/internal-supplies`, { params: { only_alerts: onlyAlerts } }),
        axios.get(`${API}/internal-supplies/recent-expense-totals`, { params: { months: 6 } }),
      ]);
      setData(a.data || { items: [], summary: {} });
      setTrend(b.data || { months: [], total: 0 });
    } catch (e) {
      toast.error("Failed to load internal supplies");
    } finally {
      setLoading(false);
    }
  }, [onlyAlerts]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.items.filter(i => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (i.name || "").toLowerCase().includes(q) || String(i.plu_code || "").includes(q);
  });

  const handleProductSearch = async (q) => {
    setProductSearch(q);
    if (q.length < 2) { setProductMatches([]); return; }
    try {
      const { data } = await axios.get(`${API}/products/paginated`, {
        params: { search: q, limit: 10, include_internal_consumables: true },
      });
      // Only show products that are NOT already flagged as internal
      const matches = (data.products || []).filter(p => !p.is_internal_consumable);
      setProductMatches(matches);
    } catch (e) { setProductMatches([]); }
  };

  const handleAddProduct = async (product) => {
    try {
      await saveWithRetry(() => axios.put(`${API}/products/${product.id}`, {
        ...product,
        is_internal_consumable: true,
      }));
      toast.success(`Added "${product.name}" to Internal Supplies`);
      setAddDialogOpen(false);
      setProductSearch("");
      setProductMatches([]);
      await load();
    } catch (e) { toast.error(errorMessage(e, "Failed to add")); }
  };

  const handleRemoveFromInternal = async (item) => {
    if (!window.confirm(`Remove "${item.name}" from Internal Supplies?\n\nIt will reappear in regular Stock Master.`)) return;
    try {
      await saveWithRetry(() => axios.put(`${API}/products/${item.id}`, {
        id: item.id,
        is_internal_consumable: false,
      }));
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(errorMessage(e, "Failed to remove")); }
  };

  const maxTrend = Math.max(1, ...trend.months.map(m => m.amount));

  return (
    <div className="p-6 w-full" data-testid="internal-supplies-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Box className="h-7 w-7 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold">Internal Supplies</h1>
            <p className="text-sm text-gray-500">Till rolls, paper, toner, cleaning — non-saleable stock the store uses itself. GRV costs auto-expense to <em>Operating Supplies</em> on the Income Statement.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} data-testid="add-internal-supply-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Product to Supplies
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Card>
          <CardContent className="pt-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Tracked items</div>
              <div className="text-2xl font-bold">{data.summary.total}</div>
            </div>
            <Box className="h-8 w-8 text-purple-400" />
          </CardContent>
        </Card>
        <Card className={data.summary.critical > 0 ? "border-red-300" : ""}>
          <CardContent className="pt-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Critical (≤ min)</div>
              <div className={`text-2xl font-bold ${data.summary.critical > 0 ? "text-red-600" : ""}`}>{data.summary.critical}</div>
            </div>
            <AlertCircle className={`h-8 w-8 ${data.summary.critical > 0 ? "text-red-500" : "text-gray-300"}`} />
          </CardContent>
        </Card>
        <Card className={data.summary.low > 0 ? "border-amber-300" : ""}>
          <CardContent className="pt-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Low (≤ reorder)</div>
              <div className={`text-2xl font-bold ${data.summary.low > 0 ? "text-amber-600" : ""}`}>{data.summary.low}</div>
            </div>
            <AlertTriangle className={`h-8 w-8 ${data.summary.low > 0 ? "text-amber-500" : "text-gray-300"}`} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <Receipt className="h-3.5 w-3.5" /> 6-mo Operating Supplies
            </div>
            <div className="text-2xl font-bold text-purple-700">R {trend.total.toFixed(0)}</div>
            <div className="flex items-end gap-0.5 mt-1 h-5">
              {trend.months.map((m, i) => (
                <div
                  key={i}
                  className="w-3 bg-purple-300 rounded-sm"
                  style={{ height: `${Math.max(2, (m.amount / maxTrend) * 100)}%` }}
                  title={`${monthNames[m.month - 1]} ${m.year}: R${m.amount.toFixed(0)}`}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base">Tracked Items</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={onlyAlerts ? "default" : "outline"}
                onClick={() => setOnlyAlerts(o => !o)}
                data-testid="only-alerts-toggle"
              >
                <TrendingDown className="h-4 w-4 mr-1" /> Only alerts
              </Button>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or PLU…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 w-64"
                  data-testid="internal-search-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Box className="h-12 w-12 mx-auto mb-2 opacity-40" />
              {data.summary.total === 0 ? (
                <>
                  <p className="font-medium">No internal supplies tracked yet.</p>
                  <p className="text-sm mt-1">Click &quot;Add Product to Supplies&quot; to flag existing items (e.g. till rolls) as internal-use.</p>
                </>
              ) : (
                <p>No items match the current filter.</p>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left">
                  <th className="px-4 py-2 w-[140px]">Status</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2 w-[100px]">PLU</th>
                  <th className="px-4 py-2 w-[140px]">Supplier</th>
                  <th className="px-4 py-2 text-right w-[120px]">Stock</th>
                  <th className="px-4 py-2 text-right w-[180px]">Min / Reorder / Max</th>
                  <th className="px-4 py-2 text-right w-[110px]">Reorder Qty</th>
                  <th className="px-4 py-2 w-[100px]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`supply-row-${item.id}`}>
                    <td className="px-4 py-2">{urgencyBadge(item.urgency)}</td>
                    <td className="px-4 py-2 font-medium">{item.name}</td>
                    <td className="px-4 py-2 text-gray-600 font-mono text-xs">{item.plu_code || "—"}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{item.supplier || "—"}</td>
                    <td className={`px-4 py-2 text-right font-mono ${item.urgency === "critical" ? "text-red-600 font-bold" : item.urgency === "low" ? "text-amber-700 font-semibold" : ""}`}>
                      {item.current_stock} {item.unit}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-gray-600">
                      {item.minimum_level} / {item.reorder_level} / {item.maximum_level}
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">
                      {item.needed_to_max > 0 ? (
                        <span className={item.urgency === "critical" || item.urgency === "low" ? "text-purple-700" : "text-gray-500"}>
                          {item.needed_to_max}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-red-600 hover:bg-red-50"
                        onClick={() => handleRemoveFromInternal(item)}
                        data-testid={`remove-${item.id}`}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Add product dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add product to Internal Supplies</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="prod-search">Search by name or PLU</Label>
            <Input
              id="prod-search"
              placeholder="e.g. till roll, A4 paper, toner…"
              value={productSearch}
              onChange={(e) => handleProductSearch(e.target.value)}
              data-testid="add-product-search"
            />
            <p className="text-xs text-gray-500">
              Pick an existing product. Its future GRV costs will book to the <em>Operating Supplies</em> expense category instead of inventory.
            </p>
          </div>
          {productMatches.length > 0 && (
            <div className="border rounded max-h-72 overflow-y-auto">
              {productMatches.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleAddProduct(p)}
                  className="w-full text-left px-3 py-2 border-b last:border-0 hover:bg-purple-50 flex items-center justify-between"
                  data-testid={`add-match-${p.id}`}
                >
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-gray-500">PLU {p.plu_code || "—"} · {p.supplier || "no supplier"}</div>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-purple-500" />
                </button>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddDialogOpen(false); setProductMatches([]); setProductSearch(""); }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
